import {CREDITS_PER_PREDICTION} from 'model-hub/signup/constants';

import type {UseCurrentUserAiCreditUsageResult} from './useCurrentUserAiCreditUsage';

export interface PredictionUsage {
  predictionsGranted: number;
  predictionsUsed: number;
  usagePercentage: number;
}

// Display-ready view of the current user's prediction usage. `loading` renders nothing;
// `unavailable` (error, no entitlement, or missing credit counts) surfaces the "no predictions
// granted" warning; `ready` carries the whole-prediction counts + percentage for the usage meter.
export type PredictionUsageState =
  | {status: 'loading'}
  | {status: 'unavailable'}
  | {status: 'ready'; usage: PredictionUsage};

// Derives whole-prediction usage from the credit-denominated entitlement. Usage is metered in AI
// credits; the Model Hub surfaces structure-prediction "predictions", which each cost
// CREDITS_PER_PREDICTION. The percentage is credit-denominated (a ratio, so it is unaffected by the
// conversion) while the counts are shown as whole predictions. Shared by the sidebar profile
// section and the Account page so both stay in sync.
export function derivePredictionUsage(result: UseCurrentUserAiCreditUsageResult): PredictionUsageState {
  const {hasError, isLoading, usage} = result;

  if (isLoading) {
    return {status: 'loading'};
  }

  if (
    hasError ||
    usage == null ||
    !usage.hasEntitlement ||
    usage.creditsUsed == null ||
    usage.creditsGranted == null
  ) {
    return {status: 'unavailable'};
  }

  const predictionsUsed = Math.round(usage.creditsUsed / CREDITS_PER_PREDICTION);
  const predictionsGranted = Math.round(usage.creditsGranted / CREDITS_PER_PREDICTION);
  const rawUsagePercentage = usage.creditsGranted > 0 ? (100 * usage.creditsUsed) / usage.creditsGranted : 0;
  const usagePercentage = Math.max(0, Math.min(100, Math.round(rawUsagePercentage)));

  return {status: 'ready', usage: {predictionsGranted, predictionsUsed, usagePercentage}};
}

// Usage at or above this share of the monthly allowance is "nearly exhausted": the point where we
// surface the contact-us link, since asking for more predictions is about to be the user's only
// option. Below it the link is noise next to a healthy usage meter. 80% lands on a whole prediction
// for both grant tiers in CREDIT_TIERS (8 of 10, 200 of 250), so the link never appears mid-prediction.
export const NEARLY_EXHAUSTED_USAGE_PERCENTAGE = 80;

// Whether to offer the user a way to ask for more predictions. `loading` is false so the affordance
// doesn't flash in and out on first paint.
export function isPredictionUsageNearlyExhausted(state: PredictionUsageState): boolean {
  // `unavailable` counts as exhausted: it renders "No predictions granted — contact Benchling for
  // help", so withholding the link there would strand exactly the users who most need it.
  if (state.status === 'unavailable') {
    return true;
  }
  return state.status === 'ready' && state.usage.usagePercentage >= NEARLY_EXHAUSTED_USAGE_PERCENTAGE;
}
